import { HttpErrorResponse } from '@angular/common/http';

/**
 * 5xx API errors are never shown to the user — the only exception is the "Deployment on the way"
 * toast. See docs/DEPLOYMENT_IN_PROGRESS_TOASTER_FRONTEND.md.
 *
 * Screens report errors from ~1200 different callbacks, so instead of guarding each one,
 * DeploymentInProgressInterceptor opens a "silent turn" just before a 5xx reaches them, and
 * SilentServerErrorToastrService drops error/warning/info toasts raised during it.
 */

/** Body `code` nginx sends (HTTP 503) only while the API restarts during a deploy. */
export const DEPLOYMENT_IN_PROGRESS_CODE = 'DEPLOYMENT_IN_PROGRESS';

/** Exact text required by the backend contract — no backend `message`, nothing appended. */
export const DEPLOYMENT_TOAST_TEXT = 'Deployment on the way';

let silentTurns = 0;

/**
 * Silences error toasts until the next macrotask. Error callbacks run synchronously when the
 * error is delivered, and promise-based callers (`firstValueFrom(...).catch`) settle in
 * microtasks before the next macrotask, so both land inside the window. Another response always
 * arrives in its own macrotask, so its toasts are unaffected.
 */
export function silenceServerErrorTurn(): void {
  silentTurns++;
  setTimeout(() => silentTurns--);
}

export function isServerErrorSilenceActive(): boolean {
  return silentTurns > 0;
}

/** True only for nginx's deploy response — never decided from the status code alone. */
export function isDeploymentInProgress(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.error?.code === DEPLOYMENT_IN_PROGRESS_CODE;
}

/** Works on the raw HttpErrorResponse and on the body ApiService rethrows (`error.error`). */
export function isServerError(error: any): boolean {
  const status = error instanceof HttpErrorResponse ? error.status : (error?.status ?? error?.status_code ?? error?.statusCode);
  return Number(status) >= 500;
}

/** For error UI that is not a toast (inline text, banners): skip it for any 5xx. */
export function shouldHideError(error: any): boolean {
  return Boolean(error?.silent) || isServerError(error) || isServerErrorSilenceActive();
}
